// components/AboutUs.js
import React, { useState, useEffect } from "react";
import styled from "styled-components";

import localImg1 from "../assets/our-story1.png"; // <- local fallback
import localImg2 from "../assets/our-story2.png"; // <- local fallback

// Local, optimized sources (rename exactly like this)
import heroAvif from "../assets/our-story1.avif";
import heroWebp from "../assets/our-story1.webp";
import heroPng from "../assets/our-story1-small.png";

import cardAvif from "../assets/our-story2.avif";
import cardWebp from "../assets/our-story2.webp";
import cardPng from "../assets/our-story2-small.png";

const EXTERNAL_1 = "https://nxteye.com/assets/videos/our-story1.png";
const EXTERNAL_2 = "https://nxteye.com/assets/videos/our-story2.png";

function useImageWithFallback(primaryUrl, fallbackUrl) {
  const [url, setUrl] = useState(fallbackUrl); // default to local so we always show something
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.onload = () => !cancelled && setUrl(primaryUrl);
    img.onerror = () => !cancelled && setUrl(fallbackUrl);
    img.src = primaryUrl;
    return () => {
      cancelled = true;
    };
  }, [primaryUrl, fallbackUrl]);
  return url;
}

const imageSet = (avif, webp, png) =>
  `image-set(url(${avif}) type("image/avif"), url(${webp}) type("image/webp"), url(${png}) type("image/png"))`;

const AboutUsSection = styled.section`
  display: flex;
  justify-content: center;
  padding: 80px 5%; /* Responsive padding */
  background: linear-gradient(
    106.71deg,
    #c69957 0%,
    #f7e096 47.6%,
    #a57632 93.27%
  );
`;

const AboutUsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 1200px;
  width: 100%;
  gap: 80px;
  position: relative;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 525px;
  height: 547px;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    width: 400px;
    height: 420px;
  }

  @media (max-width: 768px) {
    width: 320px;
    height: 340px;
  }

  @media (max-width: 480px) {
    width: 280px;
    height: 300px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 592px;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    width: 100%;
    text-align: center;
    align-items: center;
  }
`;

const SectionAboutUsLabel = styled.div`
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 16px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #000000;
`;

const Title = styled.h2`
  font-family: "Montserrat";
  font-weight: 600;
  font-size: 48px;
  line-height: 72px;
  color: #000000;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 42px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 34px;
  }
`;

const Description = styled.p`
  font-family: "Source Sans Pro";
  font-size: 18px;
  line-height: 32px;
  font-weight: 400;
  color: #000000;
  margin: 0;
`;

// Shared chrome; background is provided via `bgSet` prop using image-set()
const Frame = styled.div`
  position: absolute;
  z-index: 2;
  border-radius: 36.86px;
  overflow: hidden;
  box-shadow: 0px 4px 4px 0px #00000057;

  /* Serve AVIF/WebP/PNG with automatic fallback */
  background-image: ${(p) => p.bgSet};
  background-size: cover;
  background-position: center;
  background-color: #f3e7b3; /* subtle placeholder color while painting */

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    padding: 3px;
    border-radius: 36.86px;
    background: linear-gradient(
      258.66deg,
      #fffb90 2.12%,
      #fbec7a 14.62%,
      #f8dc65 25.19%,
      #c8a443 33.84%,
      #fae570 40.57%,
      #996c22 49.22%,
      #caaa4d 61.72%,
      #fbe878 68.45%,
      #fdf491 72.29%,
      #ffffaa 76.14%,
      #fdf491 79.02%,
      #fbe878 81.9%,
      #d0a549 90.56%,
      #a4631b 98.25%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: 1;
  }
`;

const MainImage = styled(Frame)`
  width: 378px;
  height: 363px;
  top: 0;
  left: 0;

  @media (max-width: 1024px) {
    width: 290px;
    height: 280px;
  }
  @media (max-width: 768px) {
    width: 240px;
    height: 230px;
  }
  @media (max-width: 480px) {
    width: 200px;
    height: 190px;
  }
`;

const SecondaryImage = styled(Frame)`
  width: 367px;
  height: 264px;
  top: 283px;
  left: 158px;

  @media (max-width: 1024px) {
    width: 280px;
    height: 200px;
    top: 220px;
    left: 120px;
  }
  @media (max-width: 768px) {
    width: 230px;
    height: 165px;
    top: 175px;
    left: 90px;
  }
  @media (max-width: 480px) {
    width: 190px;
    height: 135px;
    top: 150px;
    left: 70px;
  }
`;

const AboutUs = ({ data }) => {
  const bgSet1 = imageSet(heroAvif, heroWebp, heroPng);
  const bgSet2 = imageSet(cardAvif, cardWebp, cardPng);

  if (!data) return null;

  return (
    <AboutUsSection>
      <AboutUsContainer>
        <ImageContainer>
          <MainImage bgSet={bgSet1} />
          <SecondaryImage bgSet={bgSet2} />
        </ImageContainer>

        <ContentWrapper>
          <SectionAboutUsLabel>{data.label || "Our story"}</SectionAboutUsLabel>
          <Title>
            {data.title || "NxtEye is the New Language of Luxury Fashion"}
          </Title>
          <Description>
            {data.description1 ||
              "We built NxtEye to bring empathy back to the sales floor..."}
          </Description>
          <Description>
            {data.description2 ||
              "There’s no facial recognition. No tracking identities..."}
          </Description>
        </ContentWrapper>
      </AboutUsContainer>
    </AboutUsSection>
  );
};

export default AboutUs;
